import { useState, useCallback, useEffect, useRef } from "react";

import "../../assets/css/news.scss";
import Header from "../../components/header";
import NewsFooter from "../../components/news_footer";
import { getGeneralBoardList } from "../../api/board";
import { parseContent, stripHtmlTags } from "../../utils/quill_parser_util";

import imageBanner from "../../assets/images/banner4.png";

function NewsPage() {
  const [activeTab, setActiveTab] = useState("전체");
  const [selectedNews, setSelectedNews] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newsData, setNewsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 12;
  const pageGroupSize = 5;

  const modalRef = useRef(null);

  const tabs = ["전체", "공지사항", "이벤트", "FAQ"];

  const getTabType = (tab) => {
    const tabMapping = {
      전체: "ALL",
      공지사항: "NOTICE",
      이벤트: "NOTICE_EVENT",
      FAQ: "FAQ",
    };
    return tabMapping[tab] || "ALL";
  };

  const getBadgeClass = (badge) => {
    const mapping = {
      공지사항: "news-category--notice",
      이벤트: "news-category--event",
      FAQ: "news-category--faq",
    };
    return mapping[badge] || "news-category--pick";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}.${month}.${day}`;
  };

  const transformBoardData = (boardList) => {
    return boardList.map((item) => {
      const parsedContent = parseContent(item.content || "");

      let description = "";
      if (item.content) {
        description = parseContent(item.content);
      } else {
        description = parsedContent.substring(0, 300);
        if (parsedContent.length > 300) {
          description += "...";
        }
      }

      let extractedImage = null;
      let descriptionWithoutImage = description;

      const imgRegex = /<img[^>]+src=["']([^"'>]+)["']/i;
      const imgMatch = description.match(imgRegex);

      if (imgMatch && imgMatch[1]) {
        extractedImage = imgMatch[1];
        descriptionWithoutImage = description
          .replace(/<img[^>]*>/gi, "")
          .trim();
      }

      // desc에서 HTML 태그 제거하여 순수 텍스트만 표시
      const plainDesc = stripHtmlTags(descriptionWithoutImage);

      return {
        id: item.boardSeq,
        badge: item.detailType == "0032" ? "이벤트" : item.typeName,
        title: item.title || "",
        desc: plainDesc,
        fullDesc: parsedContent,
        date: formatDate(item.regDate),
        image: extractedImage,
        originalData: item,
      };
    });
  };

  const fetchNewsList = useCallback(
    async (page) => {
      setIsLoading(true);
      try {
        const type = getTabType(activeTab);
        const result = await getGeneralBoardList(type, page - 1, pageSize);

        if (result.success && result.data) {
          const transformedData = transformBoardData(result.data);
          setNewsData(transformedData);
          setTotalPages(result.totalPages || 1);
          setTotalElements(result.totalElements || 0);
        } else {
          console.error("Failed to fetch news list:", result.error);
          setNewsData([]);
          setTotalPages(1);
          setTotalElements(0);
        }
      } catch (error) {
        console.error("Error fetching news list:", error);
        setNewsData([]);
        setTotalPages(1);
        setTotalElements(0);
      } finally {
        setIsLoading(false);
      }
    },
    [activeTab, pageSize],
  );

  useEffect(() => {
    fetchNewsList(currentPage);
  }, [currentPage, fetchNewsList]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // ─── 모달 ───
  const openModal = (news) => {
    setSelectedNews(news);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = "";
    setTimeout(() => setSelectedNews(null), 200);
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && isModalOpen) closeModal();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isModalOpen]);

  // ─── 페이지네이션 ───
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPageGroup = () => {
    const startPage =
      Math.floor((currentPage - 1) / pageGroupSize) * pageGroupSize + 1;
    const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);
    return { startPage, endPage };
  };

  const { startPage, endPage } = getPageGroup();

  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const handlePrevGroup = () => {
    if (startPage > 1) setCurrentPage(startPage - 1);
  };

  const handleNextGroup = () => {
    if (endPage < totalPages) setCurrentPage(endPage + 1);
  };

  return (
    <div className="news-body">
      <Header variant="subpage" />

      {/* 뉴스 페이지 본문 */}
      <section className="news-page-section">
        <div className="news-container">
          <div className="news-page-header">
            <h1 className="news-page-title">키즈다 소식</h1>
            <p className="news-page-subtitle">
              키즈다의 최신 소식과 업데이트를 확인하세요
            </p>
          </div>

          {/* 카테고리 탭 */}
          <div className="news-tabs-v2">
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`news-tab-v2 ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div class="news-banner">
            <a
              href="https://chat.tokyai.net/join"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={imageBanner} alt="키즈다 배너" />
            </a>
          </div>

          {/* 뉴스 카드 그리드 */}
          {isLoading ? (
            <div className="news-loading-state">
              <p>소식을 불러오는 중...</p>
            </div>
          ) : newsData.length > 0 ? (
            <div className="news-page-grid">
              {newsData.map((item) => (
                <article
                  className={`news-card ${item.image ? "news-card--with-image" : ""}`}
                  key={item.id}
                >
                  <span
                    className={`news-category ${getBadgeClass(item.badge)}`}
                  >
                    {item.badge}
                  </span>
                  <div className="news-card-content">
                    <time className="news-card-date">{item.date}</time>
                    <h3 className="news-card-title">{item.title}</h3>
                    {item.image && (
                      <div className="news-card-image">
                        <img src={item.image} alt={item.title} loading="lazy" />
                      </div>
                    )}
                    <p
                      className="news-card-excerpt"
                      dangerouslySetInnerHTML={{ __html: item.desc }}
                    ></p>
                    <a
                      href="#"
                      className="news-card-link"
                      onClick={(e) => {
                        e.preventDefault();
                        openModal(item);
                      }}
                    >
                      자세히 보기 →
                    </a>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="news-empty-state">
              <p>등록된 소식이 없습니다.</p>
            </div>
          )}

          {/* 페이지네이션 */}
          {!isLoading && newsData.length > 0 && totalPages > 1 && (
            <div className="news-pagination">
              <button
                className="pagination-btn"
                onClick={handlePrevGroup}
                disabled={startPage === 1}
              >
                이전
              </button>
              <div className="pagination-numbers">
                {pageNumbers.map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`pagination-number ${
                      currentPage === pageNum ? "active" : ""
                    }`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>
              <button
                className="pagination-btn"
                onClick={handleNextGroup}
                disabled={endPage >= totalPages}
              >
                다음
              </button>
            </div>
          )}
        </div>
      </section>

      <NewsFooter variant="white" />

      {/* 뉴스 모달 */}
      <div
        className={`news-modal ${isModalOpen ? "active" : ""}`}
        ref={modalRef}
      >
        <div className="news-modal-overlay" onClick={closeModal}></div>
        <div className="news-modal-content">
          <button
            className="news-modal-close"
            aria-label="모달 닫기"
            onClick={closeModal}
          ></button>
          {selectedNews && (
            <div className="news-modal-body">
              <span
                className={`news-modal-category news-category ${getBadgeClass(selectedNews.badge)}`}
              >
                {selectedNews.badge}
              </span>
              <time className="news-modal-date">{selectedNews.date}</time>
              <h2 className="news-modal-title">{selectedNews.title}</h2>
              {selectedNews.image && (
                <div className="news-modal-image">
                  <img
                    src={selectedNews.image}
                    alt={selectedNews.title}
                    loading="lazy"
                  />
                </div>
              )}
              <div
                className="news-modal-text"
                dangerouslySetInnerHTML={{
                  __html: selectedNews.fullDesc || selectedNews.desc,
                }}
              ></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default NewsPage;
